/**
 * emotionalContinuityTracker.js — FASE R5.
 *
 * Reads from spiritualJourneyMemory (process states, 60-day) and the new
 * relationalMemoryEngine (emotional anchors) to derive DIRECTION and INTENSITY
 * of a person's emotional journey over time.
 *
 * Outputs warm directional signals — no clinical language, no timestamps.
 *
 * Direction values:
 *   recurring   — same heavy state appearing again
 *   worsening   — moved from lighter to heavier state
 *   improving   — moved from heavy to lighter state
 *   cycling     — oscillating without resolution
 *   stable_heavy — consistently heavy over time
 *   fragile     — recent improvement that is still delicate
 *   unknown     — insufficient data
 */

import { getJourneyHistory }    from "../kevia-continuity/spiritualJourneyMemory";
import { loadEmotionalMemory, PROCESS_TO_ANCHOR } from "./relationalMemoryEngine";

// Process states by weight (heavy = crisis/exhaustion, light = growth/stability)
const HEAVY_STATES    = new Set(["quebranto", "agotamiento_espiritual", "resistencia", "confusión"]);
const RECOVERY_STATES = new Set(["restauración", "crecimiento", "apertura"]);
const SEEKING_STATES  = new Set(["búsqueda"]);

/**
 * Determine directional movement from a list of ordered process states.
 * @param {string[]} states  — most recent first
 * @returns {"recurring"|"worsening"|"improving"|"cycling"|"stable_heavy"|"fragile"|"unknown"}
 */
function detectDirection(states) {
  if (!states.length || states.length < 2) return "unknown";

  const current  = states[0];
  const previous = states[1];
  const older    = states[2] ?? null;

  // Recurring: same heavy state appearing at least 2 times in last 3
  const recentHeavy = states.slice(0, 3).filter((s) => HEAVY_STATES.has(s)).length;
  if (recentHeavy >= 2 && HEAVY_STATES.has(current)) return "recurring";

  // Stable heavy: majority of all entries are heavy
  const allHeavy = states.filter((s) => HEAVY_STATES.has(s)).length;
  if (allHeavy >= states.length * 0.7 && states.length >= 3) return "stable_heavy";

  // Worsening: was recovering/seeking, now heavy
  if ((RECOVERY_STATES.has(previous) || SEEKING_STATES.has(previous)) && HEAVY_STATES.has(current)) return "worsening";

  // Improving: was heavy, now recovering
  if (HEAVY_STATES.has(previous) && (RECOVERY_STATES.has(current) || SEEKING_STATES.has(current))) {
    // Fragile if older entry was also heavy (recovery just starting)
    if (older && HEAVY_STATES.has(older)) return "fragile";
    return "improving";
  }

  // Cycling: alternating pattern
  if (older && HEAVY_STATES.has(current) && RECOVERY_STATES.has(previous) && HEAVY_STATES.has(older)) return "cycling";

  return "unknown";
}

/**
 * Derive the dominant emotional anchor from journey history.
 * If there are multiple anchors, return the most frequent among recent entries.
 * @param {string[]} states  — most recent first
 * @returns {string|null}
 */
function dominantAnchor(states) {
  if (!states.length) return null;

  const anchors = states
    .slice(0, 5)  // last 5 entries only
    .map((s) => PROCESS_TO_ANCHOR[s])
    .filter(Boolean);

  if (!anchors.length) return null;

  // Frequency count
  const freq = {};
  for (const a of anchors) freq[a] = (freq[a] ?? 0) + 1;
  return Object.entries(freq).sort((a, b) => b[1] - a[1])[0]?.[0] ?? null;
}

/**
 * Determine if a trajectory is "emotionally significant" — worth surfacing.
 * Suppresses noise when the situation is baseline/improving normally.
 * @param {string} direction
 * @param {string|null} anchor
 * @param {EmotionalMemory|null} stored
 * @returns {boolean}
 */
function isSignificant(direction, anchor, stored) {
  if (!anchor && !stored?.emotionalAnchor) return false;
  if (direction === "unknown") return false;
  if (direction === "improving" && (stored?.recurrenceCount ?? 0) < 2) return false;  // one-time improvement — don't surface
  if (direction === "fragile") return true;    // fragile recovery always significant
  if (direction === "recurring") return true;   // recurring pain always significant
  if (direction === "stable_heavy") return true;
  if (direction === "worsening") return true;
  if (direction === "cycling") return true;
  if (direction === "improving" && (stored?.recurrenceCount ?? 0) >= 2) return true;  // significant recovery from recurring struggle
  return false;
}

/**
 * Build the emotional continuity state for an entity.
 *
 * @param {{ entityId: string, entityType?: string }} entity
 * @returns {{
 *   direction:   string,
 *   anchor:      string|null,
 *   isSignificant: boolean,
 *   recurrenceCount: number,
 *   careIntensity: string,
 *   stored:      EmotionalMemory|null,
 * }}
 */
export function buildEmotionalContinuity(entity) {
  const nullResult = {
    direction: "unknown", anchor: null, isSignificant: false,
    recurrenceCount: 0, careIntensity: "light", stored: null,
  };

  if (!entity?.entityId) return nullResult;

  const journey = getJourneyHistory(entity.entityId);
  const stored  = loadEmotionalMemory(entity.entityId);

  const states    = (journey?.entries ?? []).map((e) => e.processState);
  const direction = detectDirection(states);
  const anchor    = dominantAnchor(states) ?? stored?.emotionalAnchor ?? null;

  const significant = isSignificant(direction, anchor, stored);

  return {
    direction,
    anchor,
    isSignificant:  significant,
    recurrenceCount: stored?.recurrenceCount ?? 0,
    careIntensity:   stored?.careIntensity ?? "light",
    stored,
  };
}
